import L from 'leaflet';

export class DestinationMap {
	constructor(element, options = {}) {
		this.element = element;
		this.options = {
			zoom: 12,
			minZoom: 2,
			maxZoom: 18,
			mapType: 'google', // 'osm' for OpenStreetMap, 'google' for Google Maps
			googleApiKey: '', // Add your Google Maps API key here
			zoomControl: false, // We'll add it manually in a custom position
			...options
		};
		this.map = null;
		this.bounds = null;
		this.currentLayer = null;

		// Custom marker icon: a 44px target around the 32px pin (CSS keeps the pin at its own size)
		this.markerIcon = L.icon({
			iconUrl: '/icon/map-marker.svg', // Make sure to add this SVG to your assets
			iconSize: [44, 44],
			iconAnchor: [22, 38],
			popupAnchor: [0, -40]
		});
	}

	init() {
		if (!this.element) {
			console.error('Map element not found');
			return;
		}

		// Initialize the map (wheel zoom toggled via Ctrl/Cmd — avoids trapping page scroll)
		this.map = L.map(this.element, {
			...this.options,
			scrollWheelZoom: false
		});

		// Add zoom control to the top-right
		L.control.zoom({
			position: 'topright'
		}).addTo(this.map);

		// https://schalkneethling.com/posts/dynamically-enable-and-disable-map-scroll-wheel-zoom-with-leaflet-js/
		this._onWheelModifierScrollZoom = (event) => {
			if (event.ctrlKey || event.metaKey) {
				if (!this.map.scrollWheelZoom.enabled()) {
					this.map.scrollWheelZoom.enable();
				}
			} else if (this.map.scrollWheelZoom.enabled()) {
				this.map.scrollWheelZoom.disable();
			}
		};
		this.map.getContainer().addEventListener('wheel', this._onWheelModifierScrollZoom);

		// Add the initial tile layer
		this.setMapType(this.options.mapType);

		return this;
	}

	setMapType(type) {
		if (this.currentLayer) {
			this.map.removeLayer(this.currentLayer);
		}

		switch (type) {
			case 'google':
				this.currentLayer = L.tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key={apiKey}', {
					maxZoom: 20,
					subdomains: ['mt0', 'mt1', 'mt2', 'mt3'],
					apiKey: this.options.googleApiKey || '',
					attribution: '&copy; Google Maps'
				});
				break;
			case 'osm':
			default:
				this.currentLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
					attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
				});
				break;
		}

		this.currentLayer.addTo(this.map);
		return this;
	}

	setBounds(bounds) {
		if (!bounds) {
			return this;
		}

		try {
			let south, west, north, east;

			if (Array.isArray(bounds)) {
				[[south, west], [north, east]] = bounds;
			}
			else if (typeof bounds === 'object') {
				({south, west, north, east} = bounds);
			}
			else {
				throw new Error('Invalid bounds format');
			}

			// Add padding to bounds
			const latPadding = (north - south) * 0.1; // 10% padding
			const lngPadding = (east - west) * 0.1;

			// Check if it's effectively a single point
			if (south === north && west === east) {
				this.map.setView([west, south], this.options.zoom);
			}
			else {
				this.bounds = L.latLngBounds(
					L.latLng(west - latPadding, south - lngPadding),
					L.latLng(east + latPadding, north + lngPadding)
				);
				this.map.fitBounds(this.bounds);
			}
		} catch (error) {
			console.error('Invalid bounds format:', error);
		}

		return this;
	}

	addMarker(lat, lng, options = {}) {
		if (!this.map) {
			return null;
		}

		return L.marker([lat, lng], {
			icon: this.markerIcon,
			...options
		}).addTo(this.map);
	}

	addPopup(marker, content) {
		if (!marker) {
			return;
		}

		marker.bindPopup(content, {
			closeButton: false,
			className: 'custom-popup'
		});
		return marker;
	}

	// Method to handle window resize events
	handleResize() {
		if (this.map) {
			this.map.invalidateSize();
			if (this.bounds) {
				this.map.fitBounds(this.bounds);
			}
		}
	}
}
